import './EventEditor.scss';
import { useEffect } from 'react';
import { useGetAdminTrackmanEvent } from 'queryHooks/Admin/trackmanEvents';
import { useNavigate, useParams } from 'react-router-dom';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { EventEditorForm } from '../components/EventEditorForm/EventEditorForm';
import { useTrackmanEventsEditorStore } from 'stores/Admin/Editors/TrackmanEventsEditorStore';
import { isNonEmpty } from 'utilities/isNonEmpty';
import { CreateEventPayload, UpdateEventPayload } from 'types/admin/trackmanEventsAdmin';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useServices, useStores } from 'index';
import { AdminPagesRelativeRoutes } from 'Routes';

export const EventEditor = () => {
  const eventId = useParams().eventId;
  const isEditMode = !!eventId;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const trackmanEventsEditorService = useServices().trackmanEventsEditorService;
  const notificationsStore = useStores().notificationStore;

  useSetPageTitle({ title: isEditMode ? 'Edit Event' : 'Add Event' });

  const { data: event, isPending } = useGetAdminTrackmanEvent(eventId);

  const store = useTrackmanEventsEditorStore();
  const {
    title,
    meetingUrl,
    location,
    locationType,
    startDate,
    recordingReady,
    imageUrl,
    previewImageData,
    videoUploadOption,
    videoUrl,
    videoId,
    visible,
    videoLengthHours,
    videoLengthMinutes,
    premiumEvent,
    playerType,
    setLoading,
    initAdd,
    initEdit,
    setTitle,
    setMeetingUrl,
    setLocation,
    setLocationType,
    setStartDate,
    setRecordingReady,
    setImageUrl,
    setVideoId,
    setVideoUrl,
    setVisible,
    setVideoLengthHours,
    setVideoLengthMinutes,
    setVideoUploadOption,
    setPremiumEvent,
    setPlayerType,
    resetState,
  } = store;

  useEffect(() => {
    if (isPending || !event) {
      return;
    }

    if (!event.id) {
      initAdd();
      return;
    }

    initEdit(event.id);
    setTitle(event.title);
    setMeetingUrl(event.meetingUrl);
    setLocation(event.location ?? '');
    setLocationType(event.locationType);
    setStartDate(event.startDate);
    setRecordingReady(event.recordingReady);
    setVideoId(event.videoId ?? '');
    setVideoUrl(event.videoUrl ?? '');
    setVisible(event.visible);
    setPremiumEvent(event.premiumEvent);
    setPlayerType(event.playerType ?? 'Youtube');
    setVideoUploadOption('videoId');
    setImageUrl(event.imageUrl ?? '');

    const [hours = '', minutes = ''] = (event.videoLength ?? '').split(':');
    setVideoLengthHours(hours);
    setVideoLengthMinutes(minutes);
  }, [event, isPending, isEditMode]);

  useEffect(
    () => () => {
      resetState();
    },
    []
  );

  const isPreviewImageValid = () => isNonEmpty(imageUrl) || previewImageData !== null;

  const isRecordingDataValid = () => {
    if (!recordingReady) {
      return isPreviewImageValid();
    } else {
      if (videoUploadOption === 'videoUrl') {
        return isNonEmpty(videoUrl) && isNonEmpty(videoLengthHours) && isNonEmpty(videoLengthMinutes);
      }
      return isNonEmpty(videoId) && isNonEmpty(videoLengthHours) && isNonEmpty(videoLengthMinutes);
    }
  };

  const isLocationValid = () => {
    return isNonEmpty(location) || locationType === 'Online';
  };

  const disableButton = () => {
    return !isNonEmpty(title) || !isNonEmpty(meetingUrl) || !isNonEmpty(startDate) || !isRecordingDataValid() || !isLocationValid();
  };

  const saveNewEventPayload = (): CreateEventPayload => {
    const basePayload = {
      title,
      meetingUrl,
      location,
      locationType,
      startDate,
      recordingReady,
      imageUrl,
      visible,
      premiumEvent,
      playerType,
      videoLength: `${videoLengthHours}:${videoLengthMinutes}`,
    };
    return videoUploadOption === 'videoUrl' ? { ...basePayload, videoUrl } : { ...basePayload, videoId };
  };

  const saveExistingEventPayload = (): UpdateEventPayload | null => {
    const { id } = store as { id: string };
    const basePayload = {
      id,
      title,
      meetingUrl,
      location,
      locationType,
      startDate,
      recordingReady,
      imageUrl,
      visible,
      premiumEvent,
      playerType,
      videoLength: `${videoLengthHours}:${videoLengthMinutes}`,
    };
    return videoUploadOption === 'videoUrl' ? { ...basePayload, videoUrl } : { ...basePayload, videoId };
  };

  const publishEvent = useMutation({
    mutationFn: () =>
      !isEditMode
        ? trackmanEventsEditorService.saveNewEvent(saveNewEventPayload())
        : trackmanEventsEditorService.saveExistingEvent(saveExistingEventPayload()!),
    onMutate: () => setLoading(true),
    onSuccess: async (response) => {
      if (response) {
        navigate(`/Admin/${AdminPagesRelativeRoutes.EventEditor}/${response.id}`);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['adminTrackmanEvents'] }),
        queryClient.invalidateQueries({ queryKey: ['trackmanEvents'] }),
      ]);

      notificationsStore.addToast('success', 'Event saved successfully', 'success');
    },
    onError: (error) => notificationsStore.addToast('error', `Error saving event: ${error}`, 'error'),
    onSettled: () => setLoading(false),
  });

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>{isEditMode ? 'Edit Event' : 'Add Event'}</h1>
      </div>
      <div className='admin-content-container edit-event'>
        <EventEditorForm />
        <div className='admin-footer'>
          <button className='styled-button primary' disabled={disableButton()} onClick={() => publishEvent.mutate()}>
            Publish
          </button>
        </div>
      </div>
    </>
  );
};
